import { useGetAllStudents, useGetPandaDocDocuments, useLinkPandaDocDocument } from '@actions';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import Table from '@components/V4/Table/Table';
import { useDebounce } from '@hooks';
import { PandaDocDocumentDTO } from '@shared/clientTypes/DTOs/ContractsDTO';
import useToast from 'apps/agora/src/hooks/useToast';
import { useState } from 'react';
import { formatDate, formatMoney } from '../utils';

interface PandaDocDocumentsTableProps {
  onOpenContract: (contractId: string) => void;
}

interface DocumentRowProps extends PandaDocDocumentsTableProps {
  document: PandaDocDocumentDTO;
}

const DocumentRow = (props: DocumentRowProps) => {
  const { document, onOpenContract } = props;

  const toast = useToast();
  const [studentId, setStudentId] = useState<string | undefined>(
    document.matchedStudent?._id ?? undefined
  );
  const [studentSearch, setStudentSearch] = useState('');
  const debouncedStudentSearch = useDebounce(studentSearch, 500);

  const { data: students, isLoading: isStudentsLoading } = useGetAllStudents({
    search: debouncedStudentSearch,
    limit: 20,
  });

  const { mutate: linkDocument, isLoading: isLinking } = useLinkPandaDocDocument(document._id, {
    onSuccess: () => toast.success('Document linked. The contract was created or updated.'),
    onError: (error: any) =>
      toast.error(error?.response?.data?.error?.message ?? 'The document could not be linked.'),
  });

  const fetchedOptions = (students ?? []).map((student) => ({
    label: student.fullName,
    value: student._id,
  }));

  const options =
    document.matchedStudent &&
    !fetchedOptions.some((option) => option.value === document.matchedStudent?._id)
      ? [
          {
            label: document.matchedStudent.fullName ?? 'Matched student',
            value: document.matchedStudent._id,
          },
          ...fetchedOptions,
        ]
      : fetchedOptions;

  return (
    <Table.Row>
      <Table.Cell>
        <a
          href={document.documentUrl}
          target="_blank"
          rel="noreferrer"
          className="text-customPrimary hover:underline"
        >
          {document.name || document.pandadocId}
        </a>
        <div className="text-xxs text-customGrey">
          {document.recipients
            .map((recipient) => recipient.email)
            .filter(Boolean)
            .join(', ')}
        </div>
      </Table.Cell>
      <Table.Cell>{formatDate(document.dateCompleted)}</Table.Cell>
      <Table.Cell>
        <div>{formatMoney(document.amount, document.currency ?? 'RON')}</div>
        <div className="text-xxs text-customGrey">
          {document.sessions ?? '?'} sessions ·{' '}
          {document.installmentsCount && document.installmentsCount > 1
            ? `${document.installmentsCount} installments`
            : '1 installment'}
        </div>
      </Table.Cell>
      <Table.Cell>
        {document.matchedStudent ? (
          <div>
            <div>{document.matchedStudent.fullName}</div>
            <div className="text-xxs text-customGrey">{document.matchMethod}</div>
          </div>
        ) : (
          <span className="text-customGold">Not matched</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {document.importError ? <div className="text-customRed">{document.importError}</div> : null}
        {document.warnings.length ? (
          <ul className="list-disc pl-4 text-xxs text-customGrey">
            {document.warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        ) : null}
      </Table.Cell>
      <Table.Cell>
        {document.contract ? (
          <Button
            variant="secondary"
            buttonText="Open contract"
            onClick={() => onOpenContract(document.contract as string)}
          />
        ) : (
          <div className="flex items-center gap-2">
            <Select
              showSearch
              allowClear={false}
              className="min-w-48"
              placeholder="Select student"
              options={options}
              isLoading={isStudentsLoading}
              value={studentId}
              onSearch={(value: string) => setStudentSearch(value)}
              onSelect={(value) => setStudentId(value as string)}
            />
            <Button
              variant="primary"
              buttonText="Link"
              isLoading={isLinking}
              disabled={!studentId}
              onClick={() => studentId && linkDocument({ studentId })}
            />
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const PandaDocDocumentsTable = (props: PandaDocDocumentsTableProps) => {
  const { onOpenContract } = props;

  const [onlyUnlinked, setOnlyUnlinked] = useState(true);

  const { data: documents, isLoading } = useGetPandaDocDocuments({
    onlyUnlinked: onlyUnlinked ? 'true' : 'false',
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-customGrey">
          Signed documents imported from PandaDoc. Matched documents create a contract marked for
          review; the rest can be linked to a student here.
        </p>
        <Button
          variant="secondary"
          buttonText={onlyUnlinked ? 'Show all documents' : 'Show only unlinked'}
          onClick={() => setOnlyUnlinked((value) => !value)}
        />
      </div>

      <Table isLoading={isLoading}>
        <Table.Header>
          <Table.Row isHeader>
            <Table.HeaderCell>Document</Table.HeaderCell>
            <Table.HeaderCell>Signed</Table.HeaderCell>
            <Table.HeaderCell>Extracted</Table.HeaderCell>
            <Table.HeaderCell>Student</Table.HeaderCell>
            <Table.HeaderCell>Notes</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body hasResults={!!documents?.length}>
          {documents?.map((document) => (
            <DocumentRow key={document._id} document={document} onOpenContract={onOpenContract} />
          ))}
        </Table.Body>
      </Table>
    </div>
  );
};

export default PandaDocDocumentsTable;
